import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AppointmentsHttpService, PrescriptionTypes, refreshPrescriptionsData } from '@lib/prescriptions/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTimepickerModule } from '@angular/material/timepicker';

import { PartnersService } from '@hm/core/services/partners.service';

import { SuggestionsListComponent } from '@hm/ui/lists/suggestions-list/suggestions-list.component';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';
import { formatDate } from '@lib/commons/util-functions/format-date.function';

import {
  AppointmentEventPlaceType,
  AppointmentSuggestionResponse,
  CreateAppointmentControls,
  CreateAppointmentModalType,
  PlanPrescriptionPayload,
  PlanningType,
  PrescriptionDetails,
  PrescriptionSource,
} from '@lib/core/models/create-appointment.interface';
import { InfiniteScrollResponse } from '@lib/core/models/infinite-scroll-response.interface';
import { DoctorV2, LocationV2, PartnerSearchModelV2, PartnerV2, ServiceType } from '@lib/core/models/partners.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

import { PrescriptionModalItemComponent } from '../../commons/modal-item/prescription-modal-item.component';
import { WarningMessageComponent } from '@lib/shared-ui/commons/warning-message/warning-message.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

export interface CreateAppointmentModalData {
  id?: string;
  type: CreateAppointmentModalType;
  userId?: string;
  appointmentType: AppointmentTypes;
  appointmentName: string;
  eventPlace?: AppointmentEventPlaceType;
  prescriptions: PrescriptionDetails[];
  startDate?: Date;
  partnerId?: string;
  partnerName?: string;
  address?: string;
  locationId?: string;
  doctorId?: string;
  price?: number;
}

@Component({
  selector: 'prescriptions-create-appointment-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    LoaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatError,
    MatFormField,
    MatLabel,
    MatOption,
    MatInput,
    TranslatePipe,
    MatDatepicker,
    MatDatepickerToggle,
    MatDatepickerInput,
    MatTimepickerModule,
    MatSuffix,
    MatAutocomplete,
    MatAutocompleteTrigger,
    WarningMessageComponent,
    SuggestionsListComponent,
    MatIcon,
    InfiniteScrollDirective,
    MatIconButton,
    PrescriptionModalItemComponent,
  ],
  templateUrl: './create-appointment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAppointmentModalComponent implements OnInit {
  protected readonly data: CreateAppointmentModalData = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersService);
  private readonly createAppointmentService = inject(AppointmentsHttpService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogRef = inject(MatDialogRef<CreateAppointmentModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  protected readonly modalType = CreateAppointmentModalType;

  protected createAppointmentForm!: FormGroup<CreateAppointmentControls>;

  protected readonly loading = signal<boolean>(false);
  protected readonly partnerOptions = signal<PartnerV2[]>([]);
  protected readonly locationOptions = signal<LocationV2[]>([]);
  protected readonly doctorOptions = signal<DoctorV2[]>([]);
  protected readonly selectedPartner = signal<PartnerV2 | null>(null);
  protected readonly selectedDoctor = signal<DoctorV2 | null>(null);
  protected readonly selectedSuggestion = signal<AppointmentSuggestionResponse | null>(null);

  // Pagination tokens
  protected readonly partnersContinuationToken = signal<string | null>(null);
  protected readonly locationsContinuationToken = signal<string | null>(null);
  protected readonly doctorsContinuationToken = signal<string | null>(null);

  // Search loading states
  protected readonly partnersLoading = signal<boolean>(false);
  protected readonly locationsLoading = signal<boolean>(false);
  protected readonly doctorsLoading = signal<boolean>(false);

  protected readonly type = computed<CreateAppointmentModalType>(() => this.data.type);
  protected readonly appointmentType = computed<AppointmentTypes>(() => this.data.appointmentType);
  protected readonly partnerName = computed<string>(() => this.data.partnerName);
  protected readonly address = computed<string>(() => this.data.address);
  protected readonly startDate = computed<string>(() => formatDate(this.data.startDate, true));

  protected get fc() {
    return this.createAppointmentForm.controls as CreateAppointmentControls;
  }

  ngOnInit() {
    this.initModal();
  }

  private initModal() {
    this.initForm();
    this.initPartnersSearch();
    this.initLocationsSearch();
    this.initDoctorsSearch();

    if (this.data.partnerId) this.getPartnerById(this.data.partnerId);
    else this.getPartners();
  }

  private initForm() {
    this.createAppointmentForm = this.fb.group<CreateAppointmentControls>({
      appointmentType: this.fb.control(this.data.appointmentType, [Validators.required]),
      appointmentName: this.fb.control(this.data.appointmentName, [Validators.required]),
      prescriptionIds: this.fb.control(this.data.prescriptions?.map(p => p.id) || [], [Validators.required]),
      partnerId: this.fb.control(null, [Validators.required]),
      partnerName: this.fb.control(null),
      locationId: this.fb.control(null, [Validators.required]),
      locationName: this.fb.control(null),
      doctorId: this.fb.control(null),
      doctorName: this.fb.control(null, [Validators.required]),
      date: this.fb.control(null, [Validators.required]),
      startTime: this.fb.control(null, [Validators.required]),
      endTime: this.fb.control(null, [Validators.required]),
      eventPlace: this.fb.control(this.data.eventPlace, [Validators.required]),
      price: this.fb.control(null, [Validators.required]),
    });
  }

  private initPartnersSearch() {
    this.fc.partnerName.valueChanges
      .pipe(
        debounceTime(300),
        switchMap(searchTerm => {
          this.partnersLoading.set(true);
          this.partnerOptions.set([]);
          this.partnersContinuationToken.set(null);

          return this.searchPartners(searchTerm);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: InfiniteScrollResponse<PartnerV2>) => {
          this.partnerOptions.set(result.items);
          this.partnersContinuationToken.set(result.continuationToken || null);
          this.partnersLoading.set(false);
        },
        error: () => {
          this.partnerOptions.set([]);
          this.partnersLoading.set(false);
        },
      });
  }

  private searchPartners(searchTerm: string, continuationToken?: string) {
    const partnerSearchPayload: PartnerSearchModelV2 = {
      pageSize: DEFAULT_PAGE_SIZE,
      active: true,
      searchTerm: searchTerm?.trim() || null,
      supportedServices: [ServiceType.Appointment],
      continuationToken: continuationToken,
    };
    if (!continuationToken) delete partnerSearchPayload.continuationToken;

    return this.partnersService.searchPartnersV2(partnerSearchPayload);
  }

  private getPartners() {
    this.partnersLoading.set(true);

    return this.searchPartners('').subscribe({
      next: result => {
        this.partnerOptions.set(result.items);
        this.partnersContinuationToken.set(result.continuationToken || null);
        this.partnersLoading.set(false);
      },
      error: () => this.partnersLoading.set(false),
    });
  }

  private getPartnerById(partnerId: string) {
    this.loading.set(true);

    return this.partnersService
      .getPartnerByIdV2(partnerId)
      .pipe(
        tap(result => {
          if (result) {
            this.partnerOptions.set([result]);
            this.selectPartner(result);
          }
        }),
        switchMap(result => {
          if (!result) return [];

          return this.data.locationId
            ? this.partnersService.getPartnerLocationByIdV2(this.data.partnerId, this.data.locationId).pipe(
                tap(locationResult => {
                  if (locationResult) {
                    this.locationOptions.set([locationResult]);
                    this.selectLocation(locationResult);
                  } else {
                    this.snackBar.open(this.translateService.instant('location.message.chatLocationUnavailable'), 'OK', { duration: 5000 });
                  }
                })
              )
            : this.partnersService
                .searchPartnerLocationsV2(partnerId, { partnerId: partnerId, active: true, supportedServices: [ServiceType.Appointment] })
                .pipe(tap(locationsResult => this.locationOptions.set(locationsResult.items)));
        }),
        switchMap(() => {
          if (!this.fc.partnerId.value) return [];

          return this.partnersService
            .searchPartnerDoctorsV2(this.fc.partnerId.value!, {
              partnerId: this.fc.partnerId.value!,
              pageSize: DEFAULT_PAGE_SIZE,
            })
            .pipe(
              tap(result => {
                if (result) {
                  this.doctorOptions.set(result.items);
                  this.doctorsContinuationToken.set(result.continuationToken || null);

                  if (this.data.doctorId) {
                    const targetDoctor = result.items.find(doctor => doctor.id === this.data.doctorId);

                    if (targetDoctor) this.selectDoctor(targetDoctor);
                    else console.warn('⚠️ Target doctor not found in doctors list:', this.data.doctorId);
                  }
                } else {
                  this.doctorOptions.set([]);
                  this.doctorsContinuationToken.set(null);
                }
              })
            );
        })
      )
      .subscribe({
        next: () => this.loading.set(false),
        error: () => {
          this.loading.set(false);
          this.snackBar.open(this.translateService.instant('partner.message.chatPartnerUnavailable'), 'OK', { duration: 5000 });
          this.getPartners();
        },
      });
  }

  protected fetchPartnersNextPage() {
    if (!this.partnersContinuationToken()) return;

    this.partnersLoading.set(true);
    const currentSearchTerm = this.fc.partnerName.value || '';

    this.searchPartners(currentSearchTerm, this.partnersContinuationToken()!).subscribe({
      next: (result: InfiniteScrollResponse<PartnerV2>) => {
        this.partnerOptions.update(partners => [...partners, ...result.items]);
        this.partnersContinuationToken.set(result.continuationToken || null);
        this.partnersLoading.set(false);
      },
      error: () => this.partnersLoading.set(false),
    });
  }

  private initLocationsSearch() {
    this.fc.locationName.valueChanges
      .pipe(
        debounceTime(300),
        switchMap(searchTerm => {
          if (!this.fc.partnerId.value) return [];

          this.locationsLoading.set(true);
          this.locationOptions.set([]);
          this.locationsContinuationToken.set(null);

          return this.searchLocations(this.fc.partnerId.value, searchTerm);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: InfiniteScrollResponse<LocationV2>) => {
          this.locationOptions.set(result.items);
          this.locationsContinuationToken.set(result.continuationToken || null);
          this.locationsLoading.set(false);
        },
        error: () => {
          this.locationOptions.set([]);
          this.locationsLoading.set(false);
        },
      });
  }

  private searchLocations(partnerId: string, searchTerm: string, continuationToken?: string) {
    const searchPayload = {
      partnerId: partnerId,
      active: true,
      searchTerm: searchTerm?.trim() || null,
      supportedServices: [ServiceType.Appointment],
      continuationToken: continuationToken,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    if (!continuationToken) delete searchPayload.continuationToken;

    return this.partnersService.searchPartnerLocationsV2(partnerId, searchPayload);
  }

  private getPartnerLocations(partnerId: string) {
    this.locationsLoading.set(true);
    return this.searchLocations(partnerId, '').subscribe({
      next: result => {
        this.locationOptions.set(result.items);
        this.locationsContinuationToken.set(result.continuationToken || null);
        this.locationsLoading.set(false);
      },
      error: () => this.locationsLoading.set(false),
    });
  }

  protected fetchLocationsNextPage() {
    if (!this.locationsContinuationToken() || !this.fc.partnerId.value) return;

    this.locationsLoading.set(true);
    const currentSearchTerm = this.fc.locationName.value || '';

    this.searchLocations(this.fc.partnerId.value, currentSearchTerm, this.locationsContinuationToken()!).subscribe({
      next: (result: InfiniteScrollResponse<LocationV2>) => {
        this.locationOptions.update(locations => [...locations, ...result.items]);
        this.locationsContinuationToken.set(result.continuationToken || null);
        this.locationsLoading.set(false);
      },
      error: () => this.locationsLoading.set(false),
    });
  }

  private getDoctors() {
    if (!this.fc.partnerId.value) return;

    this.doctorsLoading.set(true);

    this.partnersService
      .searchPartnerDoctorsV2(this.fc.partnerId.value, {
        partnerId: this.fc.partnerId.value,
        pageSize: DEFAULT_PAGE_SIZE,
      })
      .subscribe({
        next: result => {
          this.doctorOptions.set(result.items);
          this.doctorsContinuationToken.set(result.continuationToken || null);
          this.doctorsLoading.set(false);
        },
        error: () => {
          this.doctorOptions.set([]);
          this.doctorsLoading.set(false);
        },
      });
  }

  private initDoctorsSearch() {
    this.fc.doctorName.valueChanges
      .pipe(
        debounceTime(500),
        switchMap(searchTerm => {
          const trimmedSearchTerm = searchTerm.trim();

          if (this.selectedDoctor() && this.selectedDoctor()!.fullName !== trimmedSearchTerm) {
            this.selectedDoctor.set(null);
            this.fc.doctorId.setValue(null);
          }

          if (!this.fc.partnerId.value) return [];

          this.doctorsLoading.set(true);
          this.doctorOptions.set([]);
          this.doctorsContinuationToken.set(null);
          return this.searchDoctors(this.fc.partnerId.value, trimmedSearchTerm);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: InfiniteScrollResponse<DoctorV2>) => {
          this.doctorOptions.set(result.items);
          this.doctorsContinuationToken.set(result.continuationToken || null);
          this.doctorsLoading.set(false);
        },
        error: () => {
          this.doctorOptions.set([]);
          this.doctorsLoading.set(false);
        },
      });
  }

  private searchDoctors(partnerId: string, searchTerm: string, continuationToken?: string) {
    const searchPayload = {
      partnerId: partnerId,
      searchTerm: searchTerm?.trim() || null,
      continuationToken: continuationToken,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    if (!continuationToken) delete searchPayload.continuationToken;

    return this.partnersService.searchPartnerDoctorsV2(partnerId, searchPayload);
  }

  protected fetchDoctorsNextPage() {
    if (!this.doctorsContinuationToken() || !this.fc.partnerId.value) return;

    this.doctorsLoading.set(true);
    const currentSearchTerm = this.fc.doctorName.value?.trim() || '';

    this.searchDoctors(this.fc.partnerId.value, currentSearchTerm, this.doctorsContinuationToken()!).subscribe({
      next: (result: InfiniteScrollResponse<DoctorV2>) => {
        this.doctorOptions.update(doctors => [...doctors, ...result.items]);
        this.doctorsContinuationToken.set(result.continuationToken || null);
        this.doctorsLoading.set(false);
      },
      error: () => this.doctorsLoading.set(false),
    });
  }

  private createNonExistingDoctor(partnerId: string, doctorName: string) {
    return this.partnersService.createPartnerDoctorV2(partnerId, {
      partnerId: partnerId,
      fullName: doctorName,
    });
  }

  protected selectPartner(partner: PartnerV2) {
    this.fc.partnerId.setValue(partner.id);
    this.fc.partnerName.setValue(partner.name, { emitEvent: false });
    this.selectedPartner.set(partner);
    this.getDoctors();

    if (this.type() !== CreateAppointmentModalType.BindToExisting && !this.data.locationId) {
      this.getPartnerLocations(partner.id);
    }
  }

  protected selectLocation(location: LocationV2) {
    this.fc.locationId.setValue(location.id);
    const address = location.location?.address || '';
    const city = location.location?.city || '';
    const addressPart = address && city ? `${city}, ${address}` : city || address;
    const displayName = addressPart ? `${location.name} - ${addressPart}` : location.name;
    this.fc.locationName.setValue(displayName, { emitEvent: false });
  }

  protected selectDoctor(doctor: DoctorV2) {
    this.selectedDoctor.set(doctor);
    this.fc.doctorId.setValue(doctor.id);
    this.fc.doctorName.setValue(doctor.fullName, { emitEvent: false });
  }

  protected selectSuggestion(suggestion: AppointmentSuggestionResponse) {
    this.selectedSuggestion.set(suggestion);
  }

  protected clearPartnersSearch() {
    this.fc.partnerName.setValue('', { emitEvent: false });
    this.fc.partnerId.setValue(null);
    this.selectedPartner.set(null);
    this.partnerOptions.set([]);
    this.partnersContinuationToken.set(null);
    this.getPartners();
  }

  protected clearLocationsSearch() {
    this.fc.locationName.setValue('', { emitEvent: false });
    this.fc.locationId.setValue(null);
    this.locationOptions.set([]);
    this.locationsContinuationToken.set(null);
    if (this.fc.partnerId.value) this.getPartnerLocations(this.fc.partnerId.value);
  }

  protected clearDoctorsSearch() {
    this.fc.doctorName.setValue('', { emitEvent: false });
    this.fc.doctorId.setValue(null);
    this.selectedDoctor.set(null);
    this.doctorOptions.set([]);
    this.doctorsContinuationToken.set(null);
    this.getDoctors();
  }

  private combineDateTime(date: Date, time: Date): string {
    date.setHours(time.getHours(), time.getMinutes(), 0, 0);

    return date.toISOString();
  }

  protected submit() {
    switch (this.type()) {
      case CreateAppointmentModalType.ManualCreation:
        return this.createAppointment();
      case CreateAppointmentModalType.BindToExisting:
        return this.bindAppointment();
    }
  }

  private createAppointment() {
    this.loading.set(true);

    if (this.fc.doctorId.value) this.proceedWithAppointmentCreation(this.fc.doctorId.value);
    else {
      const doctorName = this.fc.doctorName.value?.trim();

      if (doctorName && !this.fc.doctorId.value) {
        this.createNonExistingDoctor(this.fc.partnerId.value!, doctorName).subscribe({
          next: newDoctor => {
            this.snackBar.open(this.translateService.instant('appointments.message.doctorCreatedSuccess', { doctorName }), 'OK', { duration: 3000 });
            this.proceedWithAppointmentCreation(newDoctor.id);
          },
          error: () => {
            this.snackBar.open(this.translateService.instant('appointments.message.doctorCreateError'), 'OK', { duration: 5000 });
            this.loading.set(false);
          },
        });
      } else {
        this.snackBar.open(this.translateService.instant('appointments.message.selectDoctorError'), 'OK', { duration: 5000 });
        this.loading.set(false);
      }
    }
  }

  private proceedWithAppointmentCreation(doctorId: string) {
    const payload: PlanPrescriptionPayload = {
      $type: PlanningType.Appointment,
      userId: this.data.userId,
      partnerId: this.fc.partnerId.value!,
      locationId: this.fc.locationId.value!,
      prescriptions: this.data.prescriptions || [],
      doctorId: doctorId,
      startTime: this.combineDateTime(this.fc.date.value!, this.fc.startTime.value!),
      endTime: this.combineDateTime(this.fc.date.value!, this.fc.endTime.value!),
      source: PrescriptionSource.Manual,
      price: this.fc.price.value!,
      eventPlace: AppointmentEventPlaceType.Hospital,
    };

    this.createAppointmentService.createAppointment(payload).subscribe({
      next: () => {
        this.store.dispatch(refreshPrescriptionsData());
        this.snackBar.open(this.translateService.instant('appointments.message.appointmentCreatedSuccess'), 'OK', { duration: 5000 });
        this.loading.set(false);
        this.dialogRef.close(payload);
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('appointments.message.appointmentCreateError'), 'OK', { duration: 5000 });
        this.loading.set(false);
      },
    });
  }

  private bindAppointment() {
    const suggestion = this.selectedSuggestion()!;
    const payload: PlanPrescriptionPayload = {
      $type: PlanningType.Appointment,
      userId: this.data.userId!,
      partnerId: suggestion.partnerId,
      locationId: suggestion.locationId,
      prescriptions: this.data.prescriptions || [],
      doctorId: suggestion.doctorId,
      startTime: suggestion.startDate,
      endTime: suggestion.endDate,
      source: PrescriptionSource.Suggestions,
      price: this.fc.price.value || 0,
      eventPlace: this.data.eventPlace || AppointmentEventPlaceType.Hospital,
    };
    this.loading.set(true);

    this.createAppointmentService.createAppointment(payload).subscribe({
      next: () => {
        this.store.dispatch(refreshPrescriptionsData());
        this.snackBar.open(this.translateService.instant('appointments.message.appointmentBoundSuccess'), 'OK', { duration: 5000 });
        this.loading.set(false);
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('appointments.message.appointmentBindError'), 'OK', { duration: 5000 });
        this.loading.set(false);
      },
      complete: () => this.dialogRef.close(payload),
    });
  }

  protected getPrescriptionType(appointmentType: AppointmentTypes): PrescriptionTypes {
    switch (appointmentType) {
      case AppointmentTypes.Appointment:
        return PrescriptionTypes.Appointment;
      case AppointmentTypes.InstrumentalDiagnostic:
        return PrescriptionTypes.InstrumentalDiagnostic;
      case AppointmentTypes.LaboratoryService:
        return PrescriptionTypes.LaboratoryService;
      default:
        return PrescriptionTypes.Appointment;
    }
  }
}
